import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { cloneDeep, getFromStorage } from '../../../shared/helpers/other';

export const MULTISELECT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiselectComponent),
  multi: true,
};

@Component({
  selector: 'app-multiselect',
  templateUrl: './multiselect.component.html',
  styleUrls: ['./multiselect.component.scss'],
  providers: [MULTISELECT_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class MultiselectComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
  @ViewChild('scrollTop', { static: false }) scrollTop: ElementRef;
  @Input() name: any;
  @Input() type: any;
  @Input() number: number = 0;
  @Input() isSelectAll: boolean;
  @Input() checkSelectAll: boolean;
  @Input() isSelectAllOneIndex: boolean;
  @Input() withSearchField: boolean;
  @Input() withoutAnimation: boolean;
  @Input() showSelectedNames: boolean;
  @Input() noItemsText: string = 'No options available';
  @Input() searchPlaceholder: string = 'Search';
  @Input() selectAllText: string = 'Select all';
  @Input() bindLabel: string = 'name';
  @Input() bindValue: string = 'id';
  @Input() bindValueRadio: string = '';
  @Input() prefix: string;
  @Input() closeEmmiter: EventEmitter<void>;
  @Input() isSingleselect: boolean;
  @Input() withoutCheckboxView: boolean;
  @Input() radioButtonView: boolean;
  @Input() disabled: boolean = false;
  @Input() fullObject: boolean = false;
  @Input() disableEmpty: boolean = false;
  @Input() saveAllData: boolean = false;
  @Input() needHaveEmptyRadioButton: boolean = false;
  @Input() selectedValue: any = null;
  @Input() disabledItems: any;
  @Input() persistKey: string;

  @Output() selected: EventEmitter<any> = new EventEmitter();
  @Output() closedDrop: EventEmitter<any> = new EventEmitter();
  @Output() scrolledToEnd: EventEmitter<string> = new EventEmitter<string>();
  itemList: any[];
  allItemList = [];
  allItemCheckedLength: number = 0;
  checkedItems$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  keyword$: Subject<any> = new Subject();
  openDrop: boolean = false;
  destroyed: Subject<any> = new Subject();
  isSelectedAll: boolean = false;
  onChange: any;
  onTouched: any;
  searchValue: string = '';
  value = [];
  defaultList = [];
  isSelectAllWithSearch = false;
  isInitCheckedSelectAll = false;
  @Output() private search: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private elRef: ElementRef,
    private cd: ChangeDetectorRef,
  ) {}

  @Input() set refreshData(refresh: boolean) {
    if (refresh) {
      this.defaultList = this.defaultList.map(i => ({ ...i, checked: false }));
      this.itemList = this.defaultList;
      this.allItemList = [];
      this.allItemCheckedLength = 0;
      this.isSelectAllWithSearch = false;
      this.isSelectedAll = false;
    }
  }

  @Input() set items(value) {
    value = value ? value : [];
    if (!this.defaultList?.length) {
      this.defaultList = cloneDeep(value.map(item => ({ ...item, checked: false })));
    }
    let fromStore = [];
    if (!this.isSelectAllWithSearch) {
      fromStore = getFromStorage(this.persistKey);
    }
    this.itemList = [...fromStore, ...value]
      .filter((item, index, self) => index === self.findIndex(t => t[this.bindValue] === item[this.bindValue]))
      .map(item => ({ ...item, checked: this.checkChecked(item[this.bindValue]) }));
    if (this.saveAllData) {
      this.itemList.forEach(item => {
        if (!this.allItemList.find(i => item[this.bindValue] === i[this.bindValue])) {
          this.allItemList.push(item);
        }
      });
      this.itemList = this.itemList?.map(i => {
        this.allItemList.forEach(allItem => {
          if (allItem[this.bindValue] === i[this.bindValue]) {
            i.checked = this.checkChecked(allItem[this.bindValue]);
          }
        });
        return i;
      });
      this.allItemCheckedLength = this.value?.length;
    }
    if (this.isSelectAllWithSearch) {
      this.itemList = this.itemList.map(i => ({ ...i, checked: true }));
    }

    if (this.itemList?.filter(item => item.checked).length) {
      this.checkedItems$.next(this.itemList?.filter(item => item.checked));
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event): void {
    if (!event.path.includes(this.elRef.nativeElement)) {
      this.resetSearch();
      this.openDrop = false;
    }
  }

  ngOnInit(): void {
    this.subscribeToCheckedItems();
    this.subscribeToKeyword();
    this.subscribeCloseEmitter();
  }

  ngAfterViewInit() {
    this.subscribeToScroll();
    if (this.checkSelectAll && !this.isInitCheckedSelectAll) {
      this.isInitCheckedSelectAll = true;
      this.selectAll();
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next('');
    this.destroyed.complete();
  }

  writeValue(value = []): void {
    this.value = value;
    if (this.saveAllData && !this.allItemCheckedLength) {
      this.allItemCheckedLength = value?.length;
    }
    this.itemList = this.itemList?.map(item => ({
      ...item,
      checked: this.checkChecked(item[this.bindValueRadio ? this.bindValueRadio : this.bindValue]),
    }));
    this.itemList?.forEach(item => {
      if (item.checked) {
        this.checkedItems$.next([item]);
      }
    });
    if (!this.itemList?.find(item => item.checked)) {
      this.checkedItems$.next([]);
    }
  }

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleDrop(): void {
    if (this.withSearchField) {
      this.resetSearch();
    }
    this.openDrop = !this.openDrop;
    this.sortCheckedFirst();
    if (this.openDrop) {
      this.scrollToTop();
    }
  }

  selectAll(): void {
    this.isSelectedAll = !this.isSelectedAll;
    this.isSelectAllWithSearch = !this.isSelectAllWithSearch;
    this.itemList = this.itemList.map(item => ({
      ...item,
      checked: this.isSelectAllOneIndex ? !this.isSelectAllWithSearch : !this.isSelectedAll,
    }));
    this.itemList.forEach((item, index: number) => this.toggleItem(index, item[this.bindValue]));
  }

  toggleItem(index: number, bindValue, isFromSelect?: boolean): void {
    let checkedItems;
    this.itemList = this.itemList.map(item => {
      if (item[this.bindValue] === bindValue) {
        const toggledItem = { ...item, checked: !item.checked };
        this.setToStorage(toggledItem);
        if (this.isSelectAllOneIndex && !toggledItem.checked) {
          this.isSelectAllWithSearch = false;
        }
        return toggledItem;
      } else {
        const toggledItem = this.isSingleselect ? { ...item, checked: false } : item;
        this.setToStorage(toggledItem);
        return toggledItem;
      }
    });

    if (this.type === 'radio') {
      if (this.bindValueRadio) {
        checkedItems = this.itemList[index][this.bindValueRadio];
      } else {
        checkedItems = this.itemList[index].value;
      }
      if (this.onChange) {
        this.onChange(checkedItems);
      }
      this.value = checkedItems;
      this.toggleDrop();
    } else {
      if (this.isSingleselect) {
        checkedItems = this.itemList[index].checked ? [this.itemList[index]] : [];
        this.openDrop = false;
        this.resetSearch();
        this.closedDrop.emit();
      } else {
        if (this.saveAllData) {
          this.itemList.forEach(i => {
            const value = this.allItemList.find((allItem): boolean => allItem[this.bindValue] === i[this.bindValue]);
            if (value) {
              value.checked = i.checked;
            } else {
              if (!this.allItemList.find(item => i[this.bindValue] === item[this.bindValue])) {
                this.allItemList.push(i);
              }
            }
          });
          this.allItemCheckedLength = this.allItemList.filter(i => i.checked)?.length;
          checkedItems = this.allItemList.filter(item => item.checked);
        } else {
          checkedItems = this.itemList.filter(item => item.checked);
        }
      }
      if (this.onChange) {
        if (
          (!this.withSearchField && this.isSelectAllOneIndex && this.itemList.length === checkedItems.length) ||
          (this.withSearchField && this.isSelectAllOneIndex && this.isSelectAllWithSearch)
        ) {
          this.onChange([0]);
        } else if (this.fullObject) {
          this.onChange(checkedItems);
        } else {
          this.onChange(checkedItems.map(item => item[this.bindValue]));
        }
      }
      if (this.fullObject) {
        this.value = checkedItems;
      } else {
        this.value = checkedItems.map(item => item[this.bindValue]);
      }
      if (isFromSelect && this.isSelectAll) {
        this.isSelectedAll = this.value.length === this.itemList.length;
      }
    }
    if (this.saveAllData) {
      this.isSelectedAll = this.value?.length === this.itemList.length;
      this.checkedItems$.next(this.allItemList.filter(item => item.checked));
    } else {
      this.checkedItems$.next(checkedItems);
    }
    this.cd.detectChanges();
  }

  trackByFn(index: number): number {
    return index;
  }

  checkChecked(prop): boolean {
    if (this.fullObject && this.isSingleselect) {
      return this.value && !!this.value[0] && this.value[0].id === prop;
    }
    if (this.bindValueRadio) {
      return this.value === prop;
    }
    return this.value && !(typeof this.value === 'number') && this.value && this.value.indexOf(prop) > -1;
  }

  public onSearch(value: string): void {
    this.keyword$.next(value);
  }

  public onScroll(event): void {
    if (!this.withSearchField || !this.searchValue || this.searchValue.length <= 2) {
      return;
    }
    const target = event.target;
    const scrollOffset = 20;
    const isScrolledToEnd = target.scrollTop + target.clientHeight >= target.scrollHeight - scrollOffset;
    if (isScrolledToEnd) {
      this.scrolledToEnd.emit(this.searchValue);
    }
  }

  private resetSearch(): void {
    if (this.searchValue) {
      this.onSearch('');
      this.searchValue = '';
    }
  }

  private subscribeToCheckedItems(): void {
    this.checkedItems$.pipe(takeUntil(this.destroyed)).subscribe(value => {
      this.selected.emit(value);
    });
  }

  private subscribeToKeyword(): void {
    this.keyword$.pipe(debounceTime(400), takeUntil(this.destroyed)).subscribe(value => this.search.next(value));
  }

  private subscribeCloseEmitter(): void {
    if (this.closeEmmiter) {
      this.closeEmmiter.pipe(takeUntil(this.destroyed)).subscribe(() => {
        this.openDrop = false;
        this.cd.markForCheck();
      });
    }
  }

  private subscribeToScroll(): void {
    const container = this.scrollTop?.nativeElement;
    if (container) {
      fromEvent(container, 'scroll')
        .pipe(takeUntil(this.destroyed))
        .subscribe(event => this.onScroll(event));
    }
  }

  private sortCheckedFirst() {
    if (this.itemList.find(i => i.checked)) {
      this.itemList = this.itemList.filter(item => !(item.checked === false && item.customAdded === true));
      return this.itemList?.sort((a, b): number => {
        if (a.checked === b.checked) {
          return 0;
        } else if (a.checked) {
          return -1;
        } else {
          return 1;
        }
      });
    } else if (this.itemList?.length && this.defaultList?.length) {
      this.itemList = this.defaultList;
    }
  }

  private setToStorage(item) {
    if (!this.defaultList.find(i => i[this.bindValue] === item[this.bindValue])) {
      const storageData = getFromStorage(this.persistKey);
      const findEl = storageData.find(i => i[this.bindValue] === item[this.bindValue]);
      if (findEl) {
        findEl.checked = item.checked;
      } else {
        storageData.push({ ...item, customAdded: true });
      }
      if (this.persistKey) {
        sessionStorage.setItem(this.persistKey, JSON.stringify(storageData.filter(el => el.checked)));
      }
    }
  }

  private scrollToTop() {
    const container = this.scrollTop?.nativeElement;
    if (container) {
      container.scrollTop = 0;
    }
  }
}
